import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import BarcodeScanner from '../components/BarcodeScanner';
import { CartItems } from '../components/sales-order/CartItems';
import { CustomerSelector } from '../components/sales-order/CustomerSelector';
import { OrderSummary } from '../components/sales-order/OrderSummary';
import { PriceSchemeSelector } from '../components/sales-order/PriceSchemeSelector';
import { AddItemsPanel } from '../components/sales-order/AddItemsPanel';
import { OrderDetailsForm } from '../components/sales-order/OrderDetailsForm';
import { DraftsPanel } from '../components/DraftsPanel';
import { useServerDrafts } from '../hooks/useServerDrafts';

const AUTOSAVE_KEY = 'webb_so_draft';

// R15: When any of these SKUs is added to a new SO, prompt the user:
// "Did customer bring in original for warranty claim?" The answer is stored on the line item.
const WARRANTY_PROMPT_SKUS = new Set(['01249', '5601864', '5601863', '5601865']);
const isWarrantySku = (sku) => WARRANTY_PROMPT_SKUS.has(String(sku || '').trim());

const CreateSalesOrder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') || 'confirmed';

  // R13: Form starts BLANK on every visit — no auto-load of localStorage drafts.
  // Server-side drafts are listed in the DraftsPanel and resumed only on user click.
  const saved = null;

  // Current user role — gates admin-only UI such as the markup % subtitle on each pricing scheme
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return {}; } })();
  const isAdmin = currentUser?.role === 'admin';

  const [cart, setCart] = useState(saved?.cart || []);
  const [barcode, setBarcode] = useState('');
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const [customer, setCustomer] = useState(saved?.customer || { name: '', email: '', phone: '' });
  const [customerTaxExempt, setCustomerTaxExempt] = useState(saved?.customerTaxExempt || false);
  const [loading, setLoading] = useState(false);
  const [priceSchemes, setPriceSchemes] = useState([]);
  const [selectedScheme, setSelectedScheme] = useState(saved?.selectedScheme || 'regular');

  // Product search autocomplete
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const productDropdownRef = useRef(null);
  const productSearchTimeoutRef = useRef(null);

  // Manual item state
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [manualItem, setManualItem] = useState({ description: '', quantity: 1, unit_price: 0 });

  // Customer autocomplete state
  const [customerSearch, setCustomerSearch] = useState(saved?.customerSearch || '');
  const [customerResults, setCustomerResults] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(saved?.selectedCustomer || null);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // All detail fields
  const [details, setDetails] = useState(saved?.details || {
    status: initialStatus,
    order_date: new Date().toISOString().split('T')[0],
    po_number: '', sales_rep: '', location: '', remarks: '',
    payment_terms: 'Net 30', currency: '', due_date: new Date(Date.now() + 30*86400000).toISOString().split('T')[0], requested_ship_date: '',
    billing_address1: '', billing_address2: '', billing_city: '', billing_state: '',
    billing_postal_code: '', billing_country: '', billing_remarks: '',
    ship_to_company: '', shipping_address1: '', shipping_address2: '', shipping_city: '',
    shipping_state: '', shipping_postal_code: '', shipping_country: '',
    shipping_carrier: '', shipping_tracking_number: '', shipping_remarks: '',
    taxing_scheme: 'Taxable', tax1_name: 'Sales Tax', tax1_rate: 7.25, tax2_name: '', tax2_rate: 0,
    freight: 0, lease_name: '', picked_up_by: '',
  });

  // Lease Name autocomplete
  const [leaseNameResults, setLeaseNameResults] = useState([]);
  const [showLeaseDropdown, setShowLeaseDropdown] = useState(false);
  const leaseDropdownRef = useRef(null);
  const leaseSearchTimeoutRef = useRef(null);

  const setField = (field) => (e) => setDetails({ ...details, [field]: e.target.value });
  const setNumField = (field) => (e) => setDetails({ ...details, [field]: parseFloat(e.target.value) || 0 });

  // Load price schemes
  useEffect(() => {
    const loadSchemes = async () => {
      try {
        const schemes = await api.getPriceSchemes();
        setPriceSchemes(schemes);
      } catch (err) {
        console.error('Failed to load price schemes', err);
      }
    };
    loadSchemes();
  }, []);

  // Compute the active price for a product given a pricing scheme key.
  // Looks up the scheme metadata (is_cost / markup_percent) so any admin-managed
  // scheme works correctly — not just the hardcoded 'pwo' / 'regular' pair.
  const priceForScheme = useCallback((product, schemeKey) => {
    const cost = product?.cost_price ?? product?.unit_price ?? 0;
    const scheme = priceSchemes.find(s => s.name === schemeKey);
    // PWO is always at-cost regardless of scheme metadata (business rule)
    if (schemeKey === 'pwo' || scheme?.is_cost) return cost;
    // Use the product's pre-computed price field for the scheme if available
    const precomputed = schemeKey === 'regular'
      ? (product?.unit_price ?? product?.normal_price)
      : product?.[`${schemeKey}_price`];
    if (precomputed != null) return precomputed;
    // Fallback: cost × (1 + markup%)
    const markup = scheme?.markup_percent || 0;
    return cost * (1 + markup / 100);
  }, [priceSchemes]);

  // CRITICAL: re-validate the cart's taxability flags against the live product
  // database every time the cart changes. This bulletproofs against:
  //  • stale autosave restoring is_taxable=true for products since flagged non-taxable
  //  • cached state from older versions of the form where the flag wasn't synced
  // For each cart item, look up the latest is_taxable/pwo_taxable via /api/barcode/{sku}
  // and overwrite the cart's flag.
  const lastSyncedSkusRef = useRef('');
  useEffect(() => {
    const syncTaxability = async () => {
      if (!cart || cart.length === 0) return;
      const skus = cart.map(i => i.sku).filter(Boolean).sort().join(',');
      if (skus === lastSyncedSkusRef.current) return;  // already synced this set
      lastSyncedSkusRef.current = skus;
      try {
        // Lookup each unique SKU once, in parallel
        const uniqueSkus = [...new Set(cart.map(i => i.sku).filter(Boolean))];
        const results = await Promise.all(uniqueSkus.map(sku =>
          api.request(`/api/barcode/${encodeURIComponent(sku)}`).catch(() => null)
        ));
        const flagBySku = {};
        results.forEach((r, i) => {
          if (r) {
            // CANONICAL RULE: is_taxable=false means non-taxable for everyone (including PWO).
            // pwo_taxable only narrows when is_taxable is true.
            const baseTaxable = r.is_taxable !== false;
            const pwoTaxable = baseTaxable && r.pwo_taxable !== false;
            flagBySku[uniqueSkus[i]] = { is_taxable: baseTaxable, pwo_taxable: pwoTaxable };
          }
        });
        const isPwoNow = selectedScheme === 'pwo';
        setCart(prev => prev.map(item => {
          const flags = flagBySku[item.sku];
          if (!flags) return item;
          // R6: Only PWO has the per-product taxable choice. All other customers/schemes always taxable.
          const correct = isPwoNow ? flags.pwo_taxable : true;
          if (item.is_taxable === correct) return item;
          return { ...item, is_taxable: correct };
        }));
      } catch (err) {
        console.warn('Cart taxability sync failed:', err);
      }
    };
    syncTaxability();
  }, [cart.length, selectedScheme]); // eslint-disable-line

  // Autosave form state — server-side via useServerDrafts (R13).
  // The drafts hook uses 1s debounce internally, no need for an extra timer.
  const { drafts: serverDrafts, currentDraftId, saveDraft, deleteDraft, clearCurrentDraft, setCurrentDraftId } =
    useServerDrafts('sales_order');

  // R15: Warranty claim prompt modal state.
  // When a trigger SKU is being added, the pending item is held here until the user answers.
  const [warrantyModal, setWarrantyModal] = useState(null); // { item, append }

  // R15: Watch the cart for any warranty-trigger SKU that hasn't yet been answered.
  // This intercepts every add-path (barcode, search, manual entry, scanner) without needing
  // changes at each call site. Modal stays open until the user picks Yes/No.
  useEffect(() => {
    if (warrantyModal) return; // already prompting — wait for an answer
    const idx = cart.findIndex((item) => isWarrantySku(item.sku) && item.warranty_original_returned === undefined);
    if (idx === -1) return;
    const target = cart[idx];
    setWarrantyModal({
      sku: target.sku,
      name: target.variant_name || target.name || '',
      qty: target.quantity || 1,
      onAnswer: (returned) => {
        setCart((prev) => prev.map((it, i) => (i === idx ? { ...it, warranty_original_returned: returned } : it)));
        setWarrantyModal(null);
      },
    });
  }, [cart, warrantyModal]);

  useEffect(() => {
    // Skip server-saving an empty form so we don't create useless drafts
    const hasContent = (cart && cart.length > 0) || (customerSearch || customer.name || details.lease_name);
    if (!hasContent) return;
    const label = `${customerSearch || customer.name || 'Walk-in'} • ${cart.length || 0} item${cart.length === 1 ? '' : 's'}`;
    saveDraft({
      data: { cart, customer, customerSearch, selectedCustomer, customerTaxExempt, selectedScheme, details },
      label,
    });
  }, [cart, customer, customerSearch, selectedCustomer, customerTaxExempt, selectedScheme, details, saveDraft]);

  // Resume a saved draft — populate every state field from the draft snapshot
  const handleResumeDraft = useCallback((draft) => {
    if (!draft || !draft.data || typeof draft.data !== 'object') return;
    const d = draft.data;
    if (Array.isArray(d.cart)) setCart(d.cart);
    if (d.customer && typeof d.customer === 'object') setCustomer(d.customer);
    if (typeof d.customerSearch === 'string') setCustomerSearch(d.customerSearch);
    if (d.selectedCustomer !== undefined) setSelectedCustomer(d.selectedCustomer);
    if (typeof d.customerTaxExempt === 'boolean') setCustomerTaxExempt(d.customerTaxExempt);
    if (typeof d.selectedScheme === 'string') setSelectedScheme(d.selectedScheme);
    if (d.details && typeof d.details === 'object') {
      // R13: When resuming a draft, the user is now actively building an order — promote
      // status to 'confirmed' so the dropdown matches intent and the order saves correctly.
      // (Old behavior kept 'draft' which then made the "Create Order" button silently save as draft.)
      const promoted = { ...d.details };
      if (!promoted.status || promoted.status === 'draft') promoted.status = 'confirmed';
      setDetails(promoted);
    }
    setCurrentDraftId(draft.id);
  }, [setCurrentDraftId]);

  // Auto-load a draft when the URL has ?draft=<id> (Resume from /sales-orders Drafts tab).
  // Runs once after drafts load. We strip the query param after applying so refreshes don't re-trigger.
  const draftIdFromUrl = searchParams.get('draft');
  useEffect(() => {
    if (!draftIdFromUrl || serverDrafts.length === 0) return;
    const match = serverDrafts.find(d => d.id === draftIdFromUrl);
    if (match) {
      handleResumeDraft(match);
      // Clear the param without adding to history
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('draft');
      window.history.replaceState({}, '', `${window.location.pathname}?${newParams.toString()}`.replace(/\?$/, ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional one-shot when URL param + drafts are both ready
  }, [draftIdFromUrl, serverDrafts.length]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowCustomerDropdown(false);
      }
      if (productDropdownRef.current && !productDropdownRef.current.contains(e.target)) {
        setShowProductDropdown(false);
      }
      if (leaseDropdownRef.current && !leaseDropdownRef.current.contains(e.target)) {
        setShowLeaseDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Lease Name autocomplete handler
  const handleLeaseNameChange = (e) => {
    const value = e.target.value;
    setDetails(prev => ({ ...prev, lease_name: value }));
    if (leaseSearchTimeoutRef.current) clearTimeout(leaseSearchTimeoutRef.current);
    if (value.trim().length >= 1) {
      leaseSearchTimeoutRef.current = setTimeout(async () => {
        try {
          const results = await api.getLeaseNames(value);
          setLeaseNameResults(results);
          setShowLeaseDropdown(results.length > 0);
        } catch (_err) {
          setLeaseNameResults([]);
        }
      }, 200);
    } else {
      setLeaseNameResults([]);
      setShowLeaseDropdown(false);
    }
  };

  const selectLeaseName = (name) => {
    setDetails(prev => ({ ...prev, lease_name: name }));
    setShowLeaseDropdown(false);
  };

  // Debounced customer search
  const searchCustomers = useCallback(async (query) => {
    setSearchingCustomers(true);
    try {
      const results = await api.searchCustomers(query);
      setCustomerResults(results);
      setShowCustomerDropdown(true);
    } catch (_err) {
      setCustomerResults([]);
    } finally {
      setSearchingCustomers(false);
    }
  }, []);

  const handleCustomerSearchChange = (e) => {
    const value = e.target.value;
    setCustomerSearch(value);
    setSelectedCustomer(null);
    setCustomer({ ...customer, name: value });
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => searchCustomers(value), 250);
  };

  const handleCustomerFocus = () => {
    if (customerResults.length > 0) setShowCustomerDropdown(true);
    else searchCustomers(customerSearch);
  };

  // Product search
  const handleProductSearchChange = (e) => {
    const value = e.target.value;
    setProductSearch(value);
    if (productSearchTimeoutRef.current) clearTimeout(productSearchTimeoutRef.current);
    if (value.trim().length >= 2) {
      productSearchTimeoutRef.current = setTimeout(async () => {
        setSearchingProducts(true);
        try {
          const results = await api.searchProducts(value);
          setProductResults(results);
          setShowProductDropdown(true);
        } catch (_err) {
          setProductResults([]);
        } finally {
          setSearchingProducts(false);
        }
      }, 250);
    } else {
      setProductResults([]);
      setShowProductDropdown(false);
    }
  };

  const addProductToCart = (product) => {
    // RULE: Block items with 0 stock
    if ((product.quantity_on_hand || 0) <= 0 && details.status !== 'quote') {
      alert(`Cannot add "${product.product_name}" — current stock is 0. Please update inventory/product levels first.`);
      return;
    }

    // RULE: Tax exempt items cannot have tax applied
    if (product.tax_exempt && details.taxing_scheme === 'Taxable') {
      alert(`Cannot add "${product.product_name}" — this item is tax exempt but the order is set to Taxable. Change the taxing scheme or remove tax from this item.`);
      return;
    }

    const regularPrice = product.unit_price;
    const costPrice = product.cost_price ?? product.unit_price;
    const activePrice = priceForScheme(product, selectedScheme);

    const existing = cart.find(item => item.variant_id === product.variant_id);
    if (existing) {
      setCart(cart.map(item =>
        item.variant_id === product.variant_id
          ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * item.unit_price }
          : item
      ));
    } else {
      setCart([...cart, {
        variant_id: product.variant_id,
        variant_name: `${product.product_name}`,
        sku: product.sku,
        quantity: 1,
        unit_price: activePrice,
        regular_price: regularPrice,
        cost_price: costPrice,
        total: activePrice,
        price_scheme: selectedScheme,
        is_taxable: isPwo ? (product.is_taxable !== false && product.pwo_taxable !== false) : true
      }]);
    }
    setProductSearch('');
    setProductResults([]);
    setShowProductDropdown(false);
  };

  const handleClearCustomer = () => {
    setCustomerSearch('');
    setSelectedCustomer(null);
    setCustomer({ name: '', email: '', phone: '' });
    setCustomerTaxExempt(false);
    setCustomerResults([]);
    setShowCustomerDropdown(false);
  };

  const handleSelectCustomer = (c) => {
    // Show credit denied popup immediately when selecting an unapproved customer
    if (c.credit_denied || c.approved === false) {
      alert('CREDIT IS DENIED TO THIS CUSTOMER AND ALL SALES MUST BE PAID BY CASH AS A WALK IN ONLY');
    }

    setSelectedCustomer(c);
    setCustomerSearch(c.name);
    setCustomer({ name: c.name, email: c.email || '', phone: c.phone || '' });
    setCustomerTaxExempt(c.tax_exempt || false);
    setShowCustomerDropdown(false);

    // Auto-populate payment terms and calculate due date
    const terms = c.payment_terms || 'Net 30';
    let dueDays = 30;
    const m = terms.match(/Net\s*(\d+)/i);
    if (m) dueDays = parseInt(m[1]);
    else if (terms.toLowerCase().includes('receipt') || terms.toLowerCase().includes('cod')) dueDays = 0;
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + dueDays);
    const dueDateStr = dueDate.toISOString().split('T')[0];

    // Parse billing address parts from stored data
    const billingAddr = c.billing_address || '';
    const shippingAddr = c.shipping_address || '';

    // Auto-fill all order details from customer record
    // Auto-default taxing_scheme from customer's tax info
    let customerTaxingScheme = 'Taxable';
    if (c.tax_exempt) {
      customerTaxingScheme = 'Tax Exempt';
    } else if (c.tax_scheme) {
      customerTaxingScheme = c.tax_scheme;
    } else if (c.taxing_scheme) {
      customerTaxingScheme = c.taxing_scheme;
    }

    setDetails(prev => ({
      ...prev,
      payment_terms: terms,
      due_date: dueDateStr,
      taxing_scheme: customerTaxingScheme,
      billing_address1: billingAddr,
      billing_city: c.city || '',
      billing_state: c.state || '',
      billing_postal_code: c.zip_code || '',
      billing_country: c.country || '',
      ship_to_company: c.company || c.name || '',
      shipping_address1: shippingAddr || billingAddr,
      shipping_city: c.city || '',
      shipping_state: c.state || '',
      shipping_postal_code: c.zip_code || '',
      shipping_country: c.country || '',
      billing_remarks: c.notes || prev.billing_remarks,
    }));

    if (c.pricing_scheme === 'pwo' || (c.name || '').toLowerCase().includes('patrick webb oil')) {
      setSelectedScheme('pwo');
      setCart(prev => prev.map(item => {
        if (item.cost_price !== undefined && item.cost_price !== null) {
          const newPrice = item.cost_price;
          return { ...item, unit_price: newPrice, total: item.quantity * newPrice };
        }
        return item;
      }));
    } else {
      setSelectedScheme('regular');
      setCart(prev => prev.map(item => {
        if (item.regular_price !== undefined && item.regular_price !== null) {
          const newPrice = item.regular_price;
          return { ...item, unit_price: newPrice, total: item.quantity * newPrice };
        }
        return item;
      }));
    }
  };

  const barcodeInputRef = useRef(null);
  const barcodeTimerRef = useRef(null);
  const lastKeystrokeRef = useRef(0);
  const _keystrokeBufferRef = useRef('');

  const handleBarcodeChange = (e) => {
    const val = e.target.value;
    setBarcode(val);

    // Auto-submit: detect rapid input (barcode scanner) or pasted value
    const now = Date.now();
    const _timeSinceLast = now - lastKeystrokeRef.current;
    lastKeystrokeRef.current = now;

    // If typing is very fast (< 50ms between keys = scanner), or value is long enough
    clearTimeout(barcodeTimerRef.current);
    if (val.trim().length >= 3) {
      // Short debounce: scanner sends all chars fast then we auto-submit
      barcodeTimerRef.current = setTimeout(() => {
        if (val.trim()) {
          handleBarcodeAutoAdd(val.trim());
        }
      }, 300);
    }
  };

  const handleBarcodePaste = (_e) => {
    // On paste, auto-submit after a tick
    setTimeout(() => {
      const val = barcodeInputRef.current?.value?.trim();
      if (val && val.length >= 3) {
        handleBarcodeAutoAdd(val);
      }
    }, 50);
  };

  const handleBarcodeAutoAdd = async (code) => {
    clearTimeout(barcodeTimerRef.current);
    try {
      const product = await api.lookupBarcode(code);
      const regularPrice = product.unit_price;
      const costPrice = product.cost_price ?? product.unit_price;
      const activePrice = priceForScheme(product, selectedScheme);

      setCart(prev => {
        const existing = prev.find(item => item.variant_id === product.variant_id);
        if (existing) {
          return prev.map(item =>
            item.variant_id === product.variant_id
              ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * item.unit_price }
              : item
          );
        }
        return [...prev, {
          variant_id: product.variant_id,
          variant_name: `${product.product_name} - ${product.variant_name}`,
          sku: product.sku,
          quantity: 1,
          unit_price: activePrice,
          regular_price: regularPrice,
          cost_price: costPrice,
          total: activePrice,
          price_scheme: selectedScheme,
          is_taxable: isPwo ? (product.is_taxable !== false && product.pwo_taxable !== false) : true
        }];
      });
      setBarcode('');
      // Re-focus input for next scan
      barcodeInputRef.current?.focus();
    } catch {
      // Not found — don't clear, let user see what was scanned
    }
  };

  const handleBarcodeSubmit = async (e) => {
    e.preventDefault();
    clearTimeout(barcodeTimerRef.current);
    if (!barcode.trim()) return;
    try {
      const product = await api.lookupBarcode(barcode);
      const regularPrice = product.unit_price;
      const costPrice = product.cost_price ?? product.unit_price;
      const activePrice = priceForScheme(product, selectedScheme);

      const existing = cart.find(item => item.variant_id === product.variant_id);
      if (existing) {
        setCart(cart.map(item =>
          item.variant_id === product.variant_id
            ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * item.unit_price }
            : item
        ));
      } else {
        setCart([...cart, {
          variant_id: product.variant_id,
          variant_name: `${product.product_name} - ${product.variant_name}`,
          sku: product.sku,
          quantity: 1,
          unit_price: activePrice,
          regular_price: regularPrice,
          cost_price: costPrice,
          total: activePrice,
          price_scheme: selectedScheme,
          is_taxable: isPwo ? (product.is_taxable !== false && product.pwo_taxable !== false) : true
        }]);
      }
      setBarcode('');
      barcodeInputRef.current?.focus();
    } catch (error) {
      alert('Product not found: ' + error.message);
    }
  };

  const handleManualAdd = () => {
    if (!manualItem.description) return;
    setCart([...cart, {
      variant_id: '',
      variant_name: manualItem.description,
      sku: '',
      quantity: manualItem.quantity,
      unit_price: manualItem.unit_price,
      total: manualItem.quantity * manualItem.unit_price,
      price_scheme: selectedScheme,
      is_taxable: true
    }]);
    setManualItem({ description: '', quantity: 1, unit_price: 0 });
    setShowManualAdd(false);
  };

  const updateQuantity = (index, value) => {
    const qty = Math.max(1, parseInt(value) || 1);
    setCart(cart.map((item, i) => i === index ? { ...item, quantity: qty, total: qty * item.unit_price } : item));
  };

  const updatePrice = (index, value) => {
    const price = parseFloat(value) || 0;
    setCart(cart.map((item, i) => i === index ? { ...item, unit_price: price, total: item.quantity * price } : item));
  };

  const removeItem = (index) => setCart(cart.filter((_, i) => i !== index));

  const toggleItemTax = (index) => {
    setCart(cart.map((item, i) => i === index ? { ...item, is_taxable: !item.is_taxable } : item));
  };

  const subtotal = cart.reduce((sum, item) => sum + item.total, 0);
  const taxRate = customerTaxExempt ? 0 : (details.tax1_rate || 7.25);
  // Non-taxable items must NEVER be taxed, regardless of customer type.
  // (PWO customers use pwo_taxable; everyone else uses the product's is_taxable
  // flag — both are stored in cart.is_taxable.)
  const isPwo = selectedScheme === 'pwo';
  const taxableSubtotal = cart.filter(i => i.is_taxable !== false).reduce((sum, i) => sum + i.total, 0);
  const tax = taxableSubtotal * (taxRate / 100);
  const freight = details.freight || 0;
  const total = subtotal + tax + freight;

  const submitOrder = async (statusOverride) => {
    if (cart.length === 0) return;

    if (!(details.picked_up_by || '').trim()) {
      alert('"Picked Up By" is required. Please enter who is picking up this order.');
      return;
    }

    // PWO-specific: require lease name
    const custName = customer.name || customerSearch || '';
    if (custName.toLowerCase().includes('patrick webb oil') && !(details.lease_name || '').trim()) {
      alert('Lease Name is required for all Patrick Webb Oil, LLC orders. Please enter the lease/job name.');
      return;
    }

    // Customer name is REQUIRED for ALL sales orders, no exceptions
    const finalCustName = (customer.name || customerSearch || '').trim();
    if (!finalCustName) {
      alert('A customer name is required. Please enter the customer or select an existing customer.');
      return;
    }

    if (!selectedCustomer && !isAdmin) {
      // Walk-in: require name (already enforced above)
      if (!finalCustName) {
        alert('Walk-in customer name is required. Please enter the customer name.');
        return;
      }
    }
    if (selectedCustomer && !selectedCustomer.approved && selectedCustomer.approved !== undefined && !isAdmin) {
      alert(`CREDIT IS DENIED TO THIS CUSTOMER AND ALL SALES MUST BE PAID BY CASH AS A WALK IN ONLY`);
      return;
    }
    // Block credit-denied customers for ALL users including admin
    if (selectedCustomer && selectedCustomer.credit_denied) {
      alert(`CREDIT IS DENIED TO THIS CUSTOMER AND ALL SALES MUST BE PAID BY CASH AS A WALK IN ONLY`);
      return;
    }
    // Patrick Webb Oil LLC must always use PWO cost — no admin override allowed
    if (finalCustName.toLowerCase().includes('patrick webb oil') && selectedScheme !== 'pwo') {
      alert('Patrick Webb Oil, LLC orders must use PWO cost scheme. Switching to PWO cost now.');
      setSelectedScheme('pwo');
      return;
    }
    if (selectedCustomer?.pricing_scheme === 'pwo' && selectedScheme !== 'pwo') {
      alert('This customer is set up for PWO cost pricing. Switching to PWO cost now.');
      setSelectedScheme('pwo');
      return;
    }

    const finalStatus = statusOverride || details.status || 'confirmed';

    // R15: The warranty modal at add-time already captured the answer in
    // line_item.warranty_original_returned (only for the 4 AEI SKUs). No
    // need to re-prompt at save time — that was double-asking for the same
    // 4 SKUs and felt like "every product" to the user.

    // Walk-ins must pay at time of purchase
    const isWalkin = !selectedCustomer;
    let paymentStatus = 'pending';
    let walkinPaymentType = '';

    if (isWalkin && finalStatus !== 'draft') {
      const paymentMethod = window.prompt(
        'PAYMENT REQUIRED: Payment is required at time of purchase for all walk-ins.\n\nEnter payment method:\n  1 = Cash\n  2 = Check\n  3 = Square Credit Card\n\nType 1, 2, or 3:'
      );
      if (!paymentMethod || !['1', '2', '3'].includes(paymentMethod.trim())) {
        alert('Payment method is required for walk-in orders. Please select Cash (1), Check (2), or Square Credit Card (3).');
        setLoading(false);
        return;
      }
      const methodMap = { '1': 'cash', '2': 'check', '3': 'square' };
      walkinPaymentType = methodMap[paymentMethod.trim()];
      paymentStatus = 'completed';
    }

    setLoading(true);
    try {
      await api.createSalesOrder({
        customer_name: customer.name || customerSearch || null,
        customer_email: customer.email || null,
        customer_phone: customer.phone || null,
        payment_status: paymentStatus,
        order_date: details.order_date || null,
        line_items: cart.map(item => ({
          variant_id: item.variant_id,
          variant_name: item.variant_name,
          sku: item.sku,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount: 0,
          total: item.total,
          is_taxable: item.is_taxable !== false,
          pwo_taxable: item.is_taxable !== false,
          price_scheme: item.price_scheme || selectedScheme,
        })),
        ...details,
        contact_name: details.picked_up_by || '',
        picked_up_by: details.picked_up_by || '',
        payment_method: walkinPaymentType || null,
        status: finalStatus,
        tax1_rate: customerTaxExempt ? 0 : details.tax1_rate,
        pricing_scheme: selectedScheme,
      });
      localStorage.removeItem(AUTOSAVE_KEY);
      // R13: confirmed/saved order — delete the server draft so it doesn't reappear
      if (currentDraftId) {
        try { await deleteDraft(currentDraftId); } catch { /* non-fatal */ }
      }
      clearCurrentDraft();
      navigate(finalStatus === 'quote' ? '/sales-quotes' : '/sales-orders');
    } catch (error) {
      alert('Failed to create order: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // The "Create Order" button always confirms the order — it must NEVER submit as draft.
  // (When resuming a saved draft, details.status is 'draft' from the snapshot. Without this
  // override, clicking Create Order would silently save the SO as a draft instead of confirming.)
  const handleSubmit = () => {
    const userChosen = details.status;
    // If the dropdown is still 'draft' (the default for resumed drafts), promote to 'confirmed'.
    // Other explicit choices (quote, pending, completed) are respected.
    const finalStatus = userChosen && userChosen !== 'draft' ? userChosen : 'confirmed';
    submitOrder(finalStatus);
  };
  // R13: "Save as Draft" now persists ONLY to the server `drafts` collection — no live SO record.
  // The form is reset and the user is redirected back to the SO list.
  const handleSaveDraft = async () => {
    if (cart.length === 0 && !customerSearch && !customer.name) {
      alert('Nothing to save yet — add a customer or items first.');
      return;
    }
    const label = `${customerSearch || customer.name || 'Walk-in'} • ${cart.length || 0} item${cart.length === 1 ? '' : 's'}`;
    saveDraft({
      data: { cart, customer, customerSearch, selectedCustomer, customerTaxExempt, selectedScheme, details },
      label,
    });
    // Force-flush the debounced save by waiting one tick longer than the hook's debounce
    setTimeout(() => {
      alert('Saved to your Drafts. It will NOT affect inventory until you Confirm. You can resume it anytime from the Saved Drafts panel.');
      navigate('/sales-orders');
    }, 1100);
  };

  return (
    <>
    <div className="space-y-6" data-testid="create-sales-order">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-semibold tracking-tight">New Sales Order</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - 2 cols */}
        <div className="lg:col-span-2 space-y-6">
          {/* R13: Drafts panel was moved to /sales-orders → "Drafts" tab.
              This page is now purely the form; if URL has ?draft=<id>, that draft is auto-loaded once on mount. */}

          {/* Add Items */}
          <AddItemsPanel
            showManualAdd={showManualAdd}
            setShowManualAdd={setShowManualAdd}
            manualItem={manualItem}
            setManualItem={setManualItem}
            handleManualAdd={handleManualAdd}
            barcode={barcode}
            barcodeInputRef={barcodeInputRef}
            handleBarcodeChange={handleBarcodeChange}
            handleBarcodePaste={handleBarcodePaste}
            handleBarcodeSubmit={handleBarcodeSubmit}
            onOpenCameraScanner={() => setShowCameraScanner(true)}
            productSearch={productSearch}
            handleProductSearchChange={handleProductSearchChange}
            productResults={productResults}
            searchingProducts={searchingProducts}
            showProductDropdown={showProductDropdown}
            productDropdownRef={productDropdownRef}
            addProductToCart={addProductToCart}
            selectedScheme={selectedScheme}
            priceForScheme={priceForScheme}
          />

          {/* Cart Items */}
          <CartItems
            cart={cart}
            updateQuantity={updateQuantity}
            updatePrice={updatePrice}
            removeItem={removeItem}
            toggleItemTax={toggleItemTax}
            isPwo={selectedScheme === 'pwo'}
          />

          {/* Order Details / Billing / Shipping / Tax & Freight (extracted to OrderDetailsForm) */}
          <OrderDetailsForm
            details={details}
            setField={setField}
            setNumField={setNumField}
            setDetails={setDetails}
            leaseDropdownRef={leaseDropdownRef}
            showLeaseDropdown={showLeaseDropdown}
            setShowLeaseDropdown={setShowLeaseDropdown}
            leaseNameResults={leaseNameResults}
            handleLeaseNameChange={handleLeaseNameChange}
            selectLeaseName={selectLeaseName}
          />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Price Scheme */}
          <PriceSchemeSelector
            priceSchemes={priceSchemes}
            selectedScheme={selectedScheme}
            isAdmin={isAdmin}
            onSelectScheme={(scheme) => {
              setSelectedScheme(scheme.name);
              setCart(prev => prev.map(item => {
                if (item.regular_price === undefined) return item;
                const newPrice = priceForScheme(
                  { cost_price: item.cost_price, unit_price: item.regular_price, [`${scheme.name}_price`]: item[`${scheme.name}_price`] },
                  scheme.name
                );
                return { ...item, unit_price: newPrice, total: item.quantity * newPrice, price_scheme: scheme.name };
              }));
            }}
          />

          {/* Customer */}
          <CustomerSelector
            customerSearch={customerSearch}
            handleCustomerSearchChange={handleCustomerSearchChange}
            handleCustomerFocus={handleCustomerFocus}
            showCustomerDropdown={showCustomerDropdown}
            searchingCustomers={searchingCustomers}
            customerResults={customerResults}
            handleSelectCustomer={handleSelectCustomer}
            handleClearCustomer={handleClearCustomer}
            selectedCustomer={selectedCustomer}
            customer={customer}
            setCustomer={setCustomer}
            dropdownRef={dropdownRef}
          />

          {/* Order Summary */}
          <OrderSummary
            details={details}
            setField={setField}
            subtotal={subtotal}
            taxRate={taxRate}
            customerTaxExempt={customerTaxExempt}
            taxableSubtotal={taxableSubtotal}
            tax={tax}
            freight={freight}
            total={total}
            loading={loading}
            cart={cart}
            handleSubmit={handleSubmit}
            handleSaveDraft={handleSaveDraft}
            onCancel={async () => {
              if (window.confirm('Cancel this sales order? All items will be cleared.')) {
                setCart([]);
                setSelectedCustomer(null);
                setCustomerSearch('');
                // CRITICAL: clear local autosave AND any server-side draft so the form starts fresh next time
                localStorage.removeItem(AUTOSAVE_KEY);
                if (currentDraftId) {
                  try { await deleteDraft(currentDraftId); } catch { /* non-fatal */ }
                }
                clearCurrentDraft();
                navigate('/sales-orders');
              }
            }}
          />
        </div>
      </div>
    </div>

    {/* Camera Barcode Scanner */}
    {showCameraScanner && (
      <BarcodeScanner
        onScan={async (code) => {
          setShowCameraScanner(false);
          try {
            const product = await api.lookupBarcode(code);
            const regularPrice = product.unit_price;
            const costPrice = product.cost_price ?? product.unit_price;
            const activePrice = priceForScheme(product, selectedScheme);
            const existing = cart.find(item => item.variant_id === product.variant_id);
            if (existing) {
              setCart(prev => prev.map(item =>
                item.variant_id === product.variant_id
                  ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * item.unit_price }
                  : item
              ));
            } else {
              setCart(prev => [...prev, {
                variant_id: product.variant_id,
                variant_name: `${product.product_name} - ${product.variant_name}`,
                sku: product.sku,
                quantity: 1,
                unit_price: activePrice,
                regular_price: regularPrice,
                cost_price: costPrice,
                total: activePrice,
                price_scheme: selectedScheme
              }]);
            }
          } catch {
            alert('Product not found: ' + code);
          }
        }}
        onClose={() => setShowCameraScanner(false)}
      />
    )}

    {/* R15: Warranty claim modal — fires whenever a trigger SKU is added without an answer */}
    {warrantyModal && (
      <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4" data-testid="warranty-modal">
        <div className="bg-card rounded-lg shadow-xl max-w-md w-full p-6 border border-border">
          <h3 className="text-lg font-semibold mb-2">Warranty Claim Check</h3>
          <p className="text-sm text-muted-foreground mb-1">
            Item: <span className="font-medium text-foreground">{warrantyModal.name}</span>
            {warrantyModal.sku && <span className="ml-1 font-mono text-xs">({warrantyModal.sku})</span>}
          </p>
          <p className="text-base mt-4 mb-6 font-medium">
            Did customer bring in original for warranty claim?
          </p>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => warrantyModal.onAnswer(false)}
              className="px-5 py-2 rounded-md border border-input hover:bg-muted text-sm font-medium"
              data-testid="warranty-no-btn"
            >
              No
            </button>
            <button
              type="button"
              onClick={() => warrantyModal.onAnswer(true)}
              className="px-5 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium"
              data-testid="warranty-yes-btn"
            >
              Yes — original returned
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default CreateSalesOrder;
